/**
 * Merchant-app telemetry (self-serve MVP pilot instrumentation, B10).
 *
 * Fire-and-forget pings through the existing tracking ingestion
 * (/api/funnel/track — unauthenticated, store_id-partitioned S3). Event types
 * are prefixed 'app_' — the aggregation pipeline's event-processor excludes
 * that prefix from ALL storefront analytics (sessions/visitors/funnel) and
 * counts it into the visible `app_telemetry` bucket instead, which doubles as
 * the pilot funnel readout.
 *
 * Never throws, never blocks UI — losing a ping is fine; breaking the wizard
 * over telemetry is not.
 */

import { getApiBaseUrl } from '@/config/domains'
import { authService } from '@/api/auth'

const SESSION_KEY = 'tnx_app_session'

function appSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY)
    if (!id) {
      id = `app_${crypto.randomUUID()}`
      sessionStorage.setItem(SESSION_KEY, id)
    }
    return id
  } catch {
    return 'app_unknown'
  }
}

export type AppTelemetryEvent =
  | 'proposal_viewed'
  | 'wizard_started'
  | 'wizard_abandoned'
  | 'wizard_completed'
  | 'launched'
  | 'results_viewed'

export function trackAppEvent(name: AppTelemetryEvent, data?: Record<string, unknown>): void {
  try {
    const storeId = authService.getCurrentStoreId()
    if (!storeId) return
    void fetch(`${getApiBaseUrl()}/api/funnel/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        store_id: storeId,
        event_type: `app_${name}`,
        visitor_id: `app_${storeId}`,
        session_id: appSessionId(),
        timestamp: new Date().toISOString(),
        event_data: { source: 'tnx_app', ...(data || {}) },
      }),
    }).catch(() => {})
  } catch {
    /* telemetry must never break the app */
  }
}
